// Google's consent mode, fed from the visitor's choice in HubSpot's banner. Tag Manager reads the default
// before its first tag runs, so it is set before the container loads.

import type { ConsentChoice } from '@repo/utils';

import type { TagManagerWindow } from '../tagManager';

type GoogleConsentState = 'granted' | 'denied';
type GoogleConsent = Record<string, GoogleConsentState>;

// how long Google's tags wait for the first update before they run on the default
export const WAIT_FOR_UPDATE_MS = 500;

const state = (granted: boolean): GoogleConsentState => (granted ? 'granted' : 'denied');

// Google reads consent commands only as gtag's arguments object, never as a plain array
function gtag(..._args: unknown[]): void {
  const win = window as TagManagerWindow;
  win.dataLayer = win.dataLayer || [];
  // eslint-disable-next-line prefer-rest-params
  win.dataLayer.push(arguments);
}

/** HubSpot's categories in Google's consent types; no recorded choice means everything denied. */
export function toGoogleConsent(choice: ConsentChoice | null): GoogleConsent {
  return {
    ad_storage: state(choice?.advertisement === true),
    ad_user_data: state(choice?.advertisement === true),
    ad_personalization: state(choice?.advertisement === true),
    analytics_storage: state(choice?.analytics === true),
    functionality_storage: state(choice?.functionality === true),
    personalization_storage: state(choice?.functionality === true),
  };
}

/** Everything denied but security, before Tag Manager loads. Never throws. */
export function setGoogleConsentDefaults(): void {
  try {
    gtag('consent', 'default', {
      ...toGoogleConsent(null),
      security_storage: 'granted',
      wait_for_update: WAIT_FOR_UPDATE_MS,
    });
  } catch {
    // consent stays denied
  }
}

/** Passes the recorded choice to Google's tags. Never throws. */
export function updateGoogleConsent(choice: ConsentChoice | null): void {
  try {
    gtag('consent', 'update', toGoogleConsent(choice));
  } catch {
    // consent stays as it was
  }
}
